import { Component } from '@theme/component';

/**
 * <email-signup-klaviyo>
 *
 * Progressively enhances the email-signup form to subscribe via Klaviyo's
 * modern client-side Subscriptions API instead of the native Shopify customer
 * form. Native HTML5 validation (email format + required consent) gates the
 * submit; on a valid submit we POST to Klaviyo and swap in a success/error
 * message using the block's existing `.email-signup__message` markup.
 *
 * Data attributes (set in blocks/email-signup.liquid):
 * - data-company-id      Klaviyo public API key / company_id
 * - data-list-id         Klaviyo list id to subscribe to
 * - data-revision        Klaviyo API revision (e.g. 2026-04-15)
 * - data-source          Optional custom_source for attribution
 * - data-success-message Localized success text
 * - data-error-message   Localized error text
 */
class EmailSignupKlaviyo extends Component {
  #form = null;
  #email = null;
  #message = null;
  #submitting = false;

  connectedCallback() {
    super.connectedCallback();
    this.#form = this.querySelector('form');
    this.#email = this.querySelector('input[type="email"]');
    this.#message = this.querySelector('[data-message]');
    this.#form?.addEventListener('submit', this.#handleSubmit);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#form?.removeEventListener('submit', this.#handleSubmit);
  }

  // Arrow-function field: auto-bound and holds a stable reference for
  // add/removeEventListener. (A private *method* can't be reassigned/bound.)
  #handleSubmit = async (event) => {
    // Native validation (email + required consent) has already passed when the
    // submit event fires; prevent the default navigation and send to Klaviyo.
    event.preventDefault();
    if (this.#submitting) return;

    const { companyId, listId, revision, source } = this.dataset;
    const email = this.#email?.value.trim();
    if (!companyId || !listId || !email) {
      this.#showMessage(this.dataset.errorMessage, 'error');
      return;
    }

    this.#setSubmitting(true);

    try {
      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            revision: revision || '2026-04-15',
            'content-type': 'application/json',
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: source || 'Newsletter',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      // Current revisions expect explicit consent via a subscriptions
                      // object (single opt-in). Double opt-in lists still send the
                      // confirmation email regardless of this value.
                      subscriptions: {
                        email: { marketing: { consent: 'SUBSCRIBED' } },
                      },
                    },
                  },
                },
              },
              relationships: {
                list: { data: { type: 'list', id: listId } },
              },
            },
          }),
        }
      );

      if (response.ok) {
        this.#showMessage(this.dataset.successMessage, 'success');
        this.#form?.reset();
      } else {
        this.#showMessage(this.dataset.errorMessage, 'error');
      }
    } catch (error) {
      this.#showMessage(this.dataset.errorMessage, 'error');
    } finally {
      this.#setSubmitting(false);
    }
  };

  #setSubmitting(state) {
    this.#submitting = state;
    this.toggleAttribute('data-submitting', state);
    const button = this.querySelector('.email-signup__button');
    if (button) button.disabled = state;
  }

  #showMessage(text, type) {
    if (!this.#message) return;
    this.#message.hidden = false;
    this.#message.textContent = text || '';
    this.#message.classList.toggle('email-signup__message--success', type === 'success');
    this.#message.classList.toggle('email-signup__message--error', type === 'error');
    this.#message.focus?.();
  }
}

if (!customElements.get('email-signup-klaviyo')) {
  customElements.define('email-signup-klaviyo', EmailSignupKlaviyo);
}
